/**
 * Ensures list create/update payloads get a persisted Mongo `locations._id` and richer
 * address/geo when we only have a Google Place ID (e.g. social scraper / inspo import).
 *
 * @see resolveLocationsForListGoogleRows — same resolver, PlaceCardData shape.
 */

import { fetchPersistedLocationByGooglePlaceId } from '@/api/location';
import { isMongoObjectIdString } from '@/utils/idGeneration';
import type { PlaceCardData } from '@/types/listViewTypes';

export async function enrichPlaceCardDataForListSave(place: PlaceCardData): Promise<PlaceCardData> {
  const googlePlaceId = (place.googlePlaceId || place.placeId || '').trim();
  if (!googlePlaceId) {
    return place;
  }

  if (isMongoObjectIdString(place.locationId ?? '')) {
    return place;
  }

  const addressLine =
    (typeof place.address === 'string' && place.address.trim()) ||
    (typeof place.cityName === 'string' && place.cityName.trim()) ||
    undefined;

  const resolved = await fetchPersistedLocationByGooglePlaceId(googlePlaceId, {
    name: place.title,
    addressLine,
  });

  if (!resolved?._id || !isMongoObjectIdString(resolved._id)) {
    return place;
  }

  const gp = typeof resolved.google_place_id === 'string' && resolved.google_place_id.trim()
    ? resolved.google_place_id.trim()
    : googlePlaceId;

  const formatted =
    typeof resolved.address === 'string' && resolved.address.trim()
      ? resolved.address.trim()
      : undefined;
  const a3 =
    typeof resolved.a3_intl_address === 'string' && resolved.a3_intl_address.trim()
      ? resolved.a3_intl_address.trim()
      : undefined;

  const address = a3 || formatted || place.address;

  const coords = resolved.geoposition?.coordinates;
  const longitude =
    Array.isArray(coords) && typeof coords[0] === 'number' && Number.isFinite(coords[0])
      ? coords[0]
      : place.longitude;
  const latitude =
    Array.isArray(coords) && typeof coords[1] === 'number' && Number.isFinite(coords[1])
      ? coords[1]
      : place.latitude;

  return {
    ...place,
    locationId: resolved._id,
    googlePlaceId: gp,
    placeId: gp,
    ...(address ? { address } : {}),
    ...(latitude !== undefined ? { latitude } : {}),
    ...(longitude !== undefined ? { longitude } : {}),
  };
}
